import {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
  useRef,
} from 'react'
import { FormHelperText } from '@mui/material'
import { useController, useFormContext } from 'react-hook-form'
import {
  CodeFieldset,
  DigitBox,
  DigitBoxes,
  HiddenLegend,
} from './TwoFactorCodeField.style'
import {
  enterDigits,
  getTypedDigits,
  removeDigit,
  toDigits,
  TWO_FACTOR_CODE_LENGTH,
} from './TwoFactorCodeField.utils'

const LAST_INDEX = TWO_FACTOR_CODE_LENGTH - 1

const DIGIT_INDEXES = Array.from(
  { length: TWO_FACTOR_CODE_LENGTH },
  (_, index) => index
)

interface TwoFactorCodeFieldProps {
  name: string
  label: string
  required?: boolean
  disabled?: boolean
}

// A box for each digit. The code has no gaps, so the first empty box takes the
// focus from the ones after it.
const TwoFactorCodeField = ({
  name,
  label,
  required,
  disabled,
}: TwoFactorCodeFieldProps) => {
  const { control, getValues } = useFormContext()
  const {
    field: { ref, value, onChange, onBlur },
    fieldState: { error },
  } = useController({ control, name, defaultValue: '' })
  const code: string = value ?? ''
  const boxRefs = useRef<(HTMLInputElement | null)[]>([])

  const focusBox = (index: number) =>
    boxRefs.current[Math.max(0, Math.min(index, LAST_INDEX))]?.focus()

  const enter = (index: number, digits: string) => {
    if (!digits) return
    onChange(enterDigits(code, index, digits))
    focusBox(
      digits.length >= TWO_FACTOR_CODE_LENGTH
        ? LAST_INDEX
        : index + digits.length
    )
  }

  // Backspace is handled on key down. Other deletions, e.g. with Delete, by
  // cutting or on a phone keyboard that doesn't report the key, empty the box.
  const onBoxChange =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const boxDigit = code[index] ?? ''

      if (!event.target.value && boxDigit) {
        onChange(removeDigit(code, index))
        return
      }

      enter(index, getTypedDigits(event.target.value, boxDigit))
    }

  const onFocus = (index: number) => (event: FocusEvent<HTMLInputElement>) => {
    // Read from the form, as the focus moves right after a digit is entered
    const enteredLength = (getValues(name) ?? '').length
    if (index > enteredLength) focusBox(enteredLength)
    else event.target.select()
  }

  const onKeyDown =
    (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace') {
        event.preventDefault()
        // In an empty box it deletes the digit before it
        const removedIndex = code[index] ? index : index - 1
        if (removedIndex < 0) return
        onChange(removeDigit(code, removedIndex))
        focusBox(removedIndex)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        focusBox(index - 1)
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        focusBox(index + 1)
      }
    }

  return (
    <CodeFieldset>
      <HiddenLegend>{label}</HiddenLegend>
      <DigitBoxes>
        {DIGIT_INDEXES.map(index => (
          <DigitBox
            key={index}
            value={code[index] ?? ''}
            error={!!error}
            disabled={disabled}
            autoFocus={index === 0}
            inputRef={(element: HTMLInputElement | null) => {
              boxRefs.current[index] = element
              // The form focuses the field when the code is invalid
              if (index === 0) ref(element)
            }}
            inputProps={{
              inputMode: 'numeric',
              autoComplete: index === 0 ? 'one-time-code' : 'off',
              'aria-label': `${label} ${index + 1}/${TWO_FACTOR_CODE_LENGTH}`,
              'aria-required': required,
              'aria-invalid': !!error,
            }}
            onChange={onBoxChange(index)}
            onPaste={(event: ClipboardEvent<HTMLInputElement>) => {
              event.preventDefault()
              enter(index, toDigits(event.clipboardData.getData('text')))
            }}
            onFocus={onFocus(index)}
            onKeyDown={onKeyDown(index)}
            onBlur={onBlur}
          />
        ))}
      </DigitBoxes>
      {error?.message && <FormHelperText error>{error.message}</FormHelperText>}
    </CodeFieldset>
  )
}

export default TwoFactorCodeField
